import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SpinnerService } from 'src/app/components/spinner-component/spinner.service';
import { AppConstants } from 'src/app/shared/app-constants';
import { environment } from 'src/environments/environment';
import { SessionDataService } from '../../service/session-data.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';

interface PreferenceSaveItem {
  id: number;
  enabled: boolean;
}

@Component({
  selector: 'app-preferences-dialog',
  templateUrl: './preferences-dialog.component.html',
  styleUrls: ['./preferences-dialog.component.css'],
  standalone: false
})
export class PreferencesDialogComponent implements OnInit {
  preferencesForm: FormGroup;
  userPreferences: any[] = [];
  userName: any;
  preferenceFormControls: { [key: string]: any } = {};

  constructor(
    public dialogRef: MatDialogRef<PreferencesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    public spinner: SpinnerService,
    private sessionDataService: SessionDataService,
    private toaster: ToastrService,
    private http: HttpClient
  ) {
    this.userName = this.data?.userName;
    this.preferencesForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.loadPreferences();
  }

  loadPreferences() {
    this.spinner.activate();
    
    // Get user's current preferences
    const apiUrl = `${environment.workSpaceAPI}/user/preferences/${this.data?.userName}`;
    this.http.get<any>(apiUrl).subscribe(
      (userPrefsResponse: any) => {
        this.spinner.deactivate();
        // Handle response with success, message, and data properties
        if (userPrefsResponse && userPrefsResponse.success && userPrefsResponse.data && Array.isArray(userPrefsResponse.data) && userPrefsResponse.data.length > 0) {
          // Update userPreferences array dynamically
          this.userPreferences = userPrefsResponse.data.map((pref: any) => ({
            ...pref,
            enabled: pref.enabled === true || pref.enabled === 'true'
          }));
          
          // Dynamically build form controls based on API response
          this.buildFormControls();
          
          // Update form values with current preference states
          this.updateFormValues();
        } else {
          // No user preferences found, use defaults
          this.initializeDefaultPreferences();
        }
      },
      (error) => {
        this.spinner.deactivate();
        console.error('Error loading user preferences:', error);
        this.initializeDefaultPreferences();
      }
    );
  }

  private buildFormControls() {
    // Clear existing form controls
    Object.keys(this.preferencesForm.controls).forEach(key => {
      this.preferencesForm.removeControl(key);
    });
    this.preferenceFormControls = {};

    // Dynamically create form controls for each preference
    this.userPreferences.forEach((pref: any) => {
      if (pref.name) {
        // Create a safe form control name from preference name
        const controlName = this.getFormControlName(pref.name);
        this.preferencesForm.addControl(controlName, this.fb.control(pref.enabled || false));
        this.preferenceFormControls[pref.name] = controlName;
      }
    });
  }

  private getFormControlName(preferenceName: string): string {
    // Convert preference name to a safe form control name
    // e.g., "Snapshot" -> "snapshot", "Streaming Behaviour" -> "streamingbehaviour"
    // Create a unique identifier based on preference name
    if (!preferenceName) return '';
    
    // Normalize the name: lowercase, remove special chars, replace spaces
    let controlName = preferenceName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, ''); // Remove all spaces
    
    // Ensure it's not empty and starts with a letter
    if (!controlName || !/^[a-z]/.test(controlName)) {
      controlName = 'pref' + controlName;
    }
    
    return controlName;
  }

  private updateFormValues() {
    const formValues: any = {};
    this.userPreferences.forEach((pref: any) => {
      if (pref.name && this.preferenceFormControls[pref.name]) {
        formValues[this.preferenceFormControls[pref.name]] = pref.enabled || false;
      }
    });
    this.preferencesForm.patchValue(formValues);
  }

  private initializeDefaultPreferences() {
    // Initialize default preferences structure
    this.userPreferences = [
      {
        name: 'Snapshot',
        description: 'This enables snapshot streaming by default',
        enabled: false
      },
      {
        name: 'Streaming Behaviour',
        description: 'This enables snapshot streaming keep working even we are not in the active browser tab',
        enabled: false
      }
    ];
    
    // Build form controls for default preferences
    this.buildFormControls();
    this.updateFormValues();
  }

  onPreferenceChange(preferenceName: string, enabled: boolean) {
    // Update the userPreferences array dynamically
    const prefIndex = this.userPreferences.findIndex(p => p.name === preferenceName);
    if (prefIndex !== -1) {
      this.userPreferences[prefIndex].enabled = enabled;
      
      // Update form control value dynamically
      const controlName = this.preferenceFormControls[preferenceName];
      if (controlName && this.preferencesForm.get(controlName)) {
        this.preferencesForm.patchValue({
          [controlName]: enabled
        });
      }
    } else {
      // If preference doesn't exist in userPreferences, add it
      const newPreference = {
        name: preferenceName,
        enabled: enabled,
        description: `Preference: ${preferenceName}`
      };
      
      this.userPreferences.push(newPreference);
      
      // Add form control for new preference
      const controlName = this.getFormControlName(preferenceName);
      this.preferencesForm.addControl(controlName, this.fb.control(enabled));
      this.preferenceFormControls[preferenceName] = controlName;
    }
  }

  isButtonTypePreference(preferenceName: string): boolean {
    // Determine if preference should use button UI (Snapshot/Streaming) or toggle
    // This can be based on preference name or a property from API
    return preferenceName && preferenceName.toLowerCase().includes('snapshot') && 
           !preferenceName.toLowerCase().includes('behaviour');
  }

  onClose() {
    this.dialogRef.close();
  }

  savePreferences() {
    const preferences: PreferenceSaveItem[] = [];
    this.userPreferences.forEach(pref => {
      if (pref.id) {
        let enabled = false;
        const controlName = this.preferenceFormControls[pref.name];
        if (controlName && this.preferencesForm.get(controlName)) {
          enabled = this.preferencesForm.get(controlName)?.value || false;
        } else {
          enabled = pref.enabled || false;
        }
        
        preferences.push({
          id: pref.id,
          enabled: enabled
        });
      }
    });

    this.spinner.activate();
    const apiUrl = `${environment.workSpaceAPI}/user/preferences/${this.data?.userName}`;
    let payload = {
        preferences
    }
    this.sessionDataService.saveUser(apiUrl, payload).subscribe(
      (response: any) => {
        this.spinner.deactivate();
        if (response && (response.success === true || response.status === 200)) {
          this.toaster.success("Preferences saved successfully", "");
          
          // Update local userPreferences with new enabled values dynamically
          this.userPreferences.forEach(pref => {
            const savedPref = preferences.find(sp => sp.id === pref.id);
            if (savedPref) {
              pref.enabled = savedPref.enabled;
              
              // Update form control value
              const controlName = this.preferenceFormControls[pref.name];
              if (controlName && this.preferencesForm.get(controlName)) {
                this.preferencesForm.patchValue({
                  [controlName]: savedPref.enabled
                });
              }
            }
          });
          
          this.dialogRef.close();
        } else {
          this.toaster.error(response.message || "Failed to save preferences", "");
        }
      },
      (error) => {
        this.spinner.deactivate();
        this.toaster.error("Please try again.", "");
        console.error('Error saving preferences:', error);
      }
    );
  }
}
